<script>
	import model from "../../stores/modelStore";
	import lookups from "../../stores/lookupStore";
	import {
		projectSettings,
		acceptedFileUploadList,
		yesNoUnsureOptions,
		neverSometimesAlwaysOptions
	} from "../../config";
	import { isValid } from "../../validation";
	import { unformatDate } from "../../helpers/dateHelpers";
	import { registerPageComplete } from "../../tealium";

	import Form from "../../components/Form.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import Select from "../../components/Select.svelte";
	import RadioGroup from "../../components/RadioGroup.svelte";
	import DatePicker from "../../components/DatePicker.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import TextInput from "../../components/TextInput.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import DropZone from "../../components/DropZone.svelte";
	import AutoCompleteSelector from "../../components/AutoCompleteSelector.svelte";
	import StoreList from "../../components/StoreList.svelte";
	import PageSection from "../../components/PageSection.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";
	import SaveAndClose from "../../components/SaveAndClose.svelte";

	export let pageName = null;
	export let disabled = null;
	export let pageTitle = null;
	export let persistUrl = null;
	export let lastVisitedPage = null;

	let required = true;
	let buttons = null;
	let validated = false,
		dateFinishValid = true,
		dateFinishError = false;

	const storeLookupUrl = `/api/store-lookup?q=`;

	const id = "fileUploader";
	const options = {
		url: "/api/file-upload?token=" + $model.registrationToken,
		acceptedFiles: acceptedFileUploadList,
		fileUploadToken: $model.registrationToken
	};

	if (!disabled) {
		$model.lastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}

	$model.documentList = $model.documentList || [];

	const classificationOtherId = $lookups.classification.filter((l) => l.lookupName === "Other")[0].id;

	const handleSubmit = async (e) => {
		buttons.blockNext();

		let errors = [...isValid(), ...customValidator()];

		if (!errors.length) {
			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
				console.log(response);
			} else {
				registerPageComplete(pageName, $model.contact.emailAddress, $model.contact.phoneNumber);

				buttons.next();
			}
		} else {
			errors[0].scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};

	const customValidator = () => {
		validated = true;

		if (!dateFinishValid) {
			return [dateFinishError];
		}

		return [];
	};

	const handleDateFinish = (e) => {
		dateFinishValid =
			$model.dateStartWork && e.detail.date ? e.detail.date > unformatDate($model.dateStartWork) : true;
	};

	const initialise = () => {
		dateFinishValid =
			$model.dateStartWork && $model.dateFinishWork
				? unformatDate($model.dateFinishWork) > unformatDate($model.dateStartWork)
				: true;
	};

	initialise();
</script>

<PageSection>
	<Form {disabled} id="form-action-specific" on:submit={handleSubmit}>
		<FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Work Details</h1>
			</FormColumn>
		</FormRow>

		<hr class="primary" />

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<RadioGroup
					cols="12"
					{required}
					{disabled}
					name="workAt"
					label="Did you or do you work at a KFC outlet?"
					optionList={$lookups.outletType}
					labelProperty="lookupName"
					valueProperty="id"
					bind:value={$model.outletTypeId}
				/>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<AutoCompleteSelector
					{required}
					{disabled}
					id="currentStore"
					label="What is the most recent or current KFC Outlet Location you have worked at?"
					placeholder="Start typing"
					bind:value={$model.currentStore}
					apiURL={storeLookupUrl}
				></AutoCompleteSelector>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<label>What other KFC Outlet Locations have you worked at?</label>
				<StoreList readonly={disabled} bind:tags={$model.stores}></StoreList>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<DatePicker
					{required}
					{disabled}
					label="Approximately when did you start working at a KFC outlet?"
					validator={true}
					start={new Date(1921, 0, 1)}
					end={new Date()}
					placeholder="e.g. 01/05/1987"
					bind:formattedSelected={$model.dateStartWork}
					openOnClass="calendar-icon"
				/>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<TextInput
					{required}
					{disabled}
					id="numberOfShiftsPerWeek"
					label="On average how many shifts per week did you work at KFC?"
					bind:value={$model.numberOfShiftsPerWeek}
				/>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<YesNoGroup
					{required}
					{disabled}
					name="workingStill"
					label="Do you still work at KFC?"
					bind:value={$model.workingStill}
				/>
			</FormColumn>
		</FormRow>

		{#if $model.workingStill === "No"}
			<div class:invalid={validated && !dateFinishValid}>
				<FormRow class="confirmation-box-shadow">
					<FormColumn>
						<DatePicker
							{required}
							{disabled}
							label="Approximately when did you finish working at a KFC outlet?"
							validator={true}
							start={new Date(1921, 0, 1)}
							end={new Date()}
							placeholder="e.g. 01/05/1987"
							bind:formattedSelected={$model.dateFinishWork}
							openOnClass="calendar-icon"
							on:dateChange={handleDateFinish}
							on:dateSelected={handleDateFinish}
						/>
					</FormColumn>
				</FormRow>

				<div bind:this={dateFinishError} class:error-message={validated && !dateFinishValid}>
					{#if validated && !dateFinishValid}
						Please select a valid date. Finish date cannot be earlier than start date
					{/if}
				</div>
			</div>
		{/if}

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<RadioGroup
					{required}
					{disabled}
					name="fourHoursOrGreaterShift"
					cols="12"
					label="Were the shifts generally over 4 hours?"
					optionList={yesNoUnsureOptions}
					labelProperty="label"
					valueProperty="value"
					bind:value={$model.fourHoursOrGreaterShift}
				/>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<RadioGroup
					{required}
					{disabled}
					name="paidRestBreak"
					cols="12"
					label="When working a shift over 4 hours how often did you receive a paid 10 minute break?"
					optionList={neverSometimesAlwaysOptions}
					labelProperty="label"
					valueProperty="value"
					bind:value={$model.paidRestBreak}
				/>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<RadioGroup
					{required}
					{disabled}
					name="paidDrinksBreak"
					cols="12"
					label="Were you ever told you were entitled to a 10 minute paid break on shifts over 4 hours?"
					optionList={yesNoUnsureOptions}
					labelProperty="label"
					valueProperty="value"
					bind:value={$model.paidDrinksBreak}
				/>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<YesNoGroup
					{required}
					{disabled}
					name="payslip"
					label="Do you have a payslip available from your KFC work?"
					bind:value={$model.payslip}
				/>
			</FormColumn>
		</FormRow>

		{#if $model.payslip === "Yes"}
			<FormRow class="confirmation-box-shadow">
				<FormColumn>
					<label
						>Please upload your most recent KFC payslip, if you have one available. Please also upload any
						other supporting documentation you may have available.</label
					>
					<DropZone
						{options}
						{id}
						{disabled}
						message="<strong>Upload files for your registration</strong><br />Drag files here or click to upload"
						bind:documentList={$model.documentList}
					></DropZone>
				</FormColumn>
			</FormRow>
		{/if}

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<Select
					{required}
					{disabled}
					label="Which classification most closely describes your most senior role with KFC?"
					id="classification"
					bind:value={$model.classificationId}
					optionList={$lookups.classification}
					textProperty="lookupName"
					valueProperty="id"
				/>
			</FormColumn>
		</FormRow>

		{#if $model.classificationId === classificationOtherId}
			<FormRow class="confirmation-box-shadow">
				<FormColumn>
					<TextInput
						{required}
						{disabled}
						label="Please specify other classification"
						bind:value={$model.classificationOther}
						id="classificationOther"
					/>
				</FormColumn>
			</FormRow>
		{/if}

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<TextArea
					required={false}
					{disabled}
					label="Is there anything else you want to tell us?"
					bind:value={$model.additionalInformation}
					id="additionalInformation"
				/>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<YesNoGroup
					{required}
					{disabled}
					name="confidentialStatement"
					label="Are you willing to provide a confidential statement about your experience?"
					bind:value={$model.confidentialStatement}
				/>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-box-shadow">
			<FormColumn>
				<YesNoGroup
					{required}
					{disabled}
					name="mediaShare"
					label="Are you willing to do media to share your experience?"
					bind:value={$model.mediaShare}
				/>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-hide">
			<FormColumn class="last">
				<SaveAndClose>Save and Close</SaveAndClose>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-hide">
			<FormColumn>
				<NavigationButtons bind:buttons nextPageLabel={$model.confirmationPage ? "Confirmation" : "Next"} />
			</FormColumn>
		</FormRow>
	</Form>
</PageSection>

<style>
	label span.isrequired {
		color: var(--color-error);
		left: 4px;
		position: relative;
		font-size: 1.2em;
	}
</style>
